import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Layers, Search, PlusCircle, Sparkles, ShieldCheck, Send, Ban, UserPlus,
  Eye, Loader2, CheckCircle2, AlertTriangle, XCircle, ListChecks, Pencil, History, GitBranch,
} from "lucide-react";
import { EnrichDialog, HistoryDialog, FamilyDialog, BulkFieldsDialog, GroupFamilyDialog } from "./workbenchPhase2";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

const API = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${API}${u}`);

const STATE_TONE = {
  PUBLISHED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "READY TO PUBLISH": "bg-sky-100 text-sky-800 border-sky-200",
  "NEEDS PRODUCT INTELLIGENCE": "bg-amber-100 text-amber-800 border-amber-200",
  "NEEDS IMAGE": "bg-amber-100 text-amber-800 border-amber-200",
  "NEEDS PRICING": "bg-orange-100 text-orange-800 border-orange-200",
  "NEEDS TECHNICAL REVIEW": "bg-orange-100 text-orange-800 border-orange-200",
  "UPDATE AVAILABLE": "bg-indigo-100 text-indigo-800 border-indigo-200",
  DISCONTINUED: "bg-rose-100 text-rose-800 border-rose-200",
  DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
};
const dot = (ok) => (ok ? "text-emerald-600" : "text-rose-500");

const STAT_CARDS = [
  ["total", "Total"], ["published", "Published"], ["draft", "Draft"],
  ["needs_intelligence", "Needs Intel"], ["needs_image", "Needs Images"],
  ["needs_pricing", "Needs Pricing"], ["needs_review", "Needs Review"],
  ["ready_to_publish", "Ready to Publish"], ["update_available", "Update Avail"],
  ["discontinued", "Discontinued"],
];

export default function CatalogueWorkbench() {
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ brands: [], categories: [], states: [], selling_modes: [], tasks: [] });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ brand: "", category: "", catalogue_status: "", missing: "", published: "", family_id: "" });
  const [sel, setSel] = useState([]);
  const [tab, setTab] = useState("all"); // all | mine
  const [dialog, setDialog] = useState(null); // add | qa | assign | intel | preview
  const [busy, setBusy] = useState(false);

  const loadStats = useCallback(() => {
    api.get("/catalogue-workbench/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => {
    loadStats();
    api.get("/catalogue-workbench/filters").then(({ data }) => setMeta(data)).catch(() => {});
  }, [loadStats]);

  const load = useCallback(() => {
    setLoading(true);
    if (tab === "mine") {
      api.get("/catalogue-workbench/my-tasks")
        .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
        .finally(() => setLoading(false));
      return;
    }
    api.get("/catalogue-workbench/products", { params: {
      q: q || undefined, brand: f.brand || undefined, category: f.category || undefined,
      catalogue_status: f.catalogue_status || undefined, missing: f.missing || undefined,
      published: f.published || undefined, family_id: f.family_id || undefined, limit: 100,
    } })
      .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
      .catch(() => toast.error("Could not load products"))
      .finally(() => setLoading(false));
  }, [q, f, tab]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSel = rows.length > 0 && sel.length === rows.length;
  const toggleAll = () => setSel(allSel ? [] : rows.map((r) => r.id));

  const refresh = () => { load(); loadStats(); };

  const doPublish = async (id, pub) => {
    try {
      await api.post(`/catalogue-workbench/products/${id}/${pub ? "publish" : "unpublish"}`);
      toast.success(pub ? "Published to Tenant Catalogue" : "Unpublished");
      refresh();
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d?.blockers) toast.error(`Publish blocked — ${d.blockers.length} field(s) missing`);
      else if (d?.issues) toast.error("Publish blocked — resolve QA errors");
      else toast.error(typeof d === "string" ? d : "Action failed");
    }
  };

  const bulk = async (action, payload) => {
    if (!sel.length) return toast.error("Select products first");
    if ((action === "publish" || action === "unpublish") &&
        !window.confirm(`${action === "publish" ? "Publish" : "Unpublish"} ${sel.length} product(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/catalogue-workbench/bulk", { action, ids: sel, payload });
      toast.success(`${action.replace("_", " ")} — ${data.affected} affected`);
      setSel([]); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4 max-w-[1700px]" data-testid="catalogue-workbench">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-900 text-white">
          <Layers className="h-6 w-6" strokeWidth={1.7} />
        </div>
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Catalogue</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Catalogue Workbench</h1>
          <p className="text-sm text-slate-500 mt-0.5">Build, enrich, price, QA and publish products into the Tenant Catalogue — one approved Product Master record.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={() => setTab(tab === "mine" ? "all" : "mine")} data-testid="wb-mytasks-toggle">
            <ListChecks className="h-4 w-4 mr-1.5" /> {tab === "mine" ? "All products" : "My tasks"}
          </Button>
          <Button variant="create" className="bg-slate-900" onClick={() => setDialog({ type: "add" })} data-testid="wb-add-btn">
            <PlusCircle className="h-4 w-4 mr-1.5" /> Add product
          </Button>
        </div>
      </div>
      <QuickActionGroup module="catalogue" />

      {/* Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 xl:grid-cols-10 gap-2" data-testid="wb-stats">
          {STAT_CARDS.map(([k, label]) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`wb-stat-${k}`}>
              <div className="text-lg font-semibold text-slate-900">{stats[k] ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
            </div>
          ))}
        </div>
      )}
      {stats && (
        <div className="text-xs text-slate-500">Catalogue completion: <span className="font-semibold text-slate-800">{stats.completion_pct}%</span> · <span data-testid="wb-stat-families">{stats.families ?? 0} product families</span></div>
      )}

      {f.family_id && (
        <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-[12px] text-indigo-900" data-testid="wb-family-filter">
          <GitBranch className="h-3.5 w-3.5" /> Showing variants of one family
          <button className="ml-auto underline" onClick={() => setF({ ...f, family_id: "" })} data-testid="wb-family-filter-clear">Show all products</button>
        </div>
      )}

      {/* Filters */}
      {tab === "all" && (
        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search part number, SKU, name, brand…" className="pl-9 h-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wb-search" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={f.catalogue_status || "all"} onValueChange={(v) => setF({ ...f, catalogue_status: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-[210px] text-[12px]" data-testid="wb-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.brand || "all"} onValueChange={(v) => setF({ ...f, brand: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-[160px] text-[12px]" data-testid="wb-filter-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="all">All brands</SelectItem>{meta.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.category || "all"} onValueChange={(v) => setF({ ...f, category: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-[160px] text-[12px]" data-testid="wb-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="all">All categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.missing || "all"} onValueChange={(v) => setF({ ...f, missing: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-[150px] text-[12px]" data-testid="wb-filter-missing"><SelectValue placeholder="Missing data" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any data</SelectItem>
                <SelectItem value="image">Missing image</SelectItem>
                <SelectItem value="pricing">Missing pricing</SelectItem>
                <SelectItem value="intelligence">Missing intelligence</SelectItem>
                <SelectItem value="documents">Missing documents</SelectItem>
              </SelectContent>
            </Select>
            <Select value={f.published || "all"} onValueChange={(v) => setF({ ...f, published: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-[150px] text-[12px]" data-testid="wb-filter-published"><SelectValue placeholder="Published" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Published + not</SelectItem><SelectItem value="true">Published</SelectItem><SelectItem value="false">Unpublished</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Bulk bar */}
      {sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-slate-900 text-white rounded-md px-3 py-2" data-testid="wb-bulk-bar">
          <span className="text-[12px] font-medium">{sel.length} selected</span>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => setDialog({ type: "qa", ids: sel })} data-testid="wb-bulk-qa">Run QA</Button>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => bulk("publish")} disabled={busy} data-testid="wb-bulk-publish">Publish</Button>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => bulk("unpublish")} disabled={busy} data-testid="wb-bulk-unpublish">Unpublish</Button>
          <Select onValueChange={(v) => bulk("set_visibility", { tenant_visibility: v })}>
            <SelectTrigger className="h-7 w-[150px] text-[12px] text-slate-900 bg-white" data-testid="wb-bulk-visibility"><SelectValue placeholder="Set visibility" /></SelectTrigger>
            <SelectContent>{["ALL", "SELECTED_TENANTS", "HIDDEN", "QUOTE_ONLY", "ORDERABLE"].map((v) => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulk("set_selling_mode", { selling_mode: v })}>
            <SelectTrigger className="h-7 w-[160px] text-[12px] text-slate-900 bg-white" data-testid="wb-bulk-selling"><SelectValue placeholder="Selling mode" /></SelectTrigger>
            <SelectContent>{meta.selling_modes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => setDialog({ type: "assign", ids: sel })} data-testid="wb-bulk-assign">Assign</Button>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => setDialog({ type: "bulk_fields", ids: sel })} data-testid="wb-bulk-fields">Set fields</Button>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => setDialog({ type: "group_family", ids: sel })} data-testid="wb-bulk-group-family"><GitBranch className="h-3 w-3 mr-1" />Group as family</Button>
          <button className="ml-auto text-[12px] underline" onClick={() => setSel([])}>Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left"><input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="wb-select-all" /></th>
              <th className="px-3 py-2.5 text-left">Product</th>
              <th className="px-3 py-2.5 text-left hidden lg:table-cell">Brand</th>
              <th className="px-3 py-2.5 text-left hidden xl:table-cell">Part Number</th>
              <th className="px-3 py-2.5 text-left hidden xl:table-cell">Category</th>
              <th className="px-3 py-2.5 text-center hidden md:table-cell">Intel</th>
              <th className="px-3 py-2.5 text-center hidden md:table-cell">Img</th>
              <th className="px-3 py-2.5 text-center hidden md:table-cell">Price</th>
              <th className="px-3 py-2.5 text-left">Status</th>
              <th className="px-3 py-2.5 text-center hidden lg:table-cell">Complete</th>
              <th className="px-3 py-2.5 text-left hidden xl:table-cell">Assigned</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400" data-testid="wb-empty">No products match these filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`wb-row-${r.id}`}>
                <td className="px-3 py-2"><input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggleSel(r.id)} data-testid={`wb-select-${r.id}`} /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 shrink-0 rounded bg-slate-50 border border-slate-100 grid place-items-center overflow-hidden">
                      {r.primary_image?.url ? <img src={imgUrl(r.primary_image.url)} alt="" className="h-full w-full object-contain" /> : <Layers className="h-4 w-4 text-slate-300" />}
                    </div>
                    <div className="min-w-0"><div className="font-medium text-slate-900 truncate max-w-[280px]">{r.product_name}</div>
                      <div className="lg:hidden text-[11px] text-slate-500">{r.brand} · {r.part_number}</div>
                      {r.family_id && (
                        <button className="inline-flex items-center gap-1 text-[10px] text-indigo-700 hover:underline" onClick={() => setF({ ...f, family_id: r.family_id })} data-testid={`wb-family-chip-${r.id}`}>
                          <GitBranch className="h-3 w-3" />{r.family_size} variant{r.family_size === 1 ? "" : "s"}{Object.keys(r.variant_attributes || {}).length ? ` · ${Object.values(r.variant_attributes).join(" / ")}` : ""}
                        </button>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 hidden lg:table-cell">{r.brand || "—"}</td>
                <td className="px-3 py-2 hidden xl:table-cell font-mono text-xs">{r.part_number || "—"}</td>
                <td className="px-3 py-2 hidden xl:table-cell">{r.category || "—"}</td>
                <td className="px-3 py-2 text-center hidden md:table-cell"><CheckCircle2 className={`h-4 w-4 inline ${dot(r.intelligence_status === "ENRICHED")}`} /></td>
                <td className="px-3 py-2 text-center hidden md:table-cell"><CheckCircle2 className={`h-4 w-4 inline ${dot(r.image_status === "APPROVED")}`} /></td>
                <td className="px-3 py-2 text-center hidden md:table-cell"><CheckCircle2 className={`h-4 w-4 inline ${dot(r.pricing_status === "SET")}`} /></td>
                <td className="px-3 py-2"><Badge className={`text-[10px] border ${STATE_TONE[r.catalogue_status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{r.catalogue_status}</Badge></td>
                <td className="px-3 py-2 text-center hidden lg:table-cell">
                  <span className={`text-xs font-semibold ${r.completeness >= 100 ? "text-emerald-700" : r.completeness >= 70 ? "text-amber-600" : "text-rose-600"}`}>{r.completeness}%</span>
                </td>
                <td className="px-3 py-2 hidden xl:table-cell text-xs text-slate-600">{r.assigned_to ? <span>{r.assigned_to.split("@")[0]}<div className="text-[10px] text-slate-400">{r.assigned_task}</div></span> : "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <button title="Enrich / edit" onClick={() => setDialog({ type: "enrich", id: r.id })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-enrich-${r.id}`}><Pencil className="h-3.5 w-3.5 text-slate-700" /></button>
                    <button title="Variants / family" onClick={() => setDialog({ type: "family", row: r })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-family-${r.id}`}><GitBranch className={`h-3.5 w-3.5 ${r.family_id ? "text-indigo-700" : "text-slate-500"}`} /></button>
                    <button title="Version history" onClick={() => setDialog({ type: "history", row: r })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-history-${r.id}`}><History className="h-3.5 w-3.5 text-slate-600" /></button>
                    <button title="Preview as tenant" onClick={() => setDialog({ type: "preview", id: r.id })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-preview-${r.id}`}><Eye className="h-3.5 w-3.5 text-slate-600" /></button>
                    <button title="Fetch product intelligence" onClick={() => setDialog({ type: "intel", row: r })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-intel-${r.id}`}><Sparkles className="h-3.5 w-3.5 text-indigo-600" /></button>
                    <button title="Run QA" onClick={() => setDialog({ type: "qa", ids: [r.id] })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-qa-${r.id}`}><ShieldCheck className="h-3.5 w-3.5 text-slate-600" /></button>
                    <button title="Assign" onClick={() => setDialog({ type: "assign", ids: [r.id] })} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" data-testid={`wb-assign-${r.id}`}><UserPlus className="h-3.5 w-3.5 text-slate-600" /></button>
                    {r.published ? (
                      <button title="Unpublish" onClick={() => doPublish(r.id, false)} className="h-7 px-2 grid place-items-center rounded hover:bg-rose-50 text-rose-600" data-testid={`wb-unpublish-${r.id}`}><Ban className="h-3.5 w-3.5" /></button>
                    ) : (
                      <button title="Publish" onClick={() => doPublish(r.id, true)} className="h-7 px-2 grid place-items-center rounded hover:bg-emerald-50 text-emerald-700" data-testid={`wb-publish-${r.id}`}><Send className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500">{total.toLocaleString()} product(s){tab === "all" ? " · showing up to 100" : ""}</div>

      {dialog?.type === "add" && <AddDialog meta={meta} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "qa" && <QADialog ids={dialog.ids} onClose={() => setDialog(null)} />}
      {dialog?.type === "assign" && <AssignDialog ids={dialog.ids} tasks={meta.tasks} onClose={() => setDialog(null)} onDone={() => { setSel([]); refresh(); }} />}
      {dialog?.type === "intel" && <IntelDialog row={dialog.row} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "preview" && <PreviewDialog id={dialog.id} onClose={() => setDialog(null)} />}
      {dialog?.type === "enrich" && <EnrichDialog id={dialog.id} meta={meta} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "history" && <HistoryDialog row={dialog.row} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "family" && <FamilyDialog row={dialog.row} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "bulk_fields" && <BulkFieldsDialog ids={dialog.ids} onClose={() => setDialog(null)} onDone={() => { setSel([]); refresh(); }} />}
      {dialog?.type === "group_family" && <GroupFamilyDialog ids={dialog.ids} onClose={() => setDialog(null)} onDone={() => { setSel([]); refresh(); }} />}
      <datalist id="wb-brands">{meta.brands.map((b) => <option key={b} value={b} />)}</datalist>
      <datalist id="wb-cats">{meta.categories.map((c) => <option key={c} value={c} />)}</datalist>
    </div>
  );
}

function AddDialog({ meta, onClose, onDone }) {
  const [form, setForm] = useState({ product_name: "", brand: "", sku: "", category: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (force = false) => {
    if (!form.product_name.trim()) return toast.error("Product name is required");
    setBusy(true); setDup(null);
    try {
      const { data } = await api.post("/catalogue-workbench/products", { ...form, force_variant: force });
      toast.success(`Added ${data.product.product_name} (Draft)`);
      onDone(); onClose();
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d?.code === "DUPLICATE_PART_NUMBER") { setDup(d.existing); toast.error("Part number already exists"); }
      else toast.error(typeof d === "string" ? d : "Could not add product");
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="wb-add-dialog">
        <DialogHeader>
          <DialogTitle>Add product</DialogTitle>
          <DialogDescription>Creates a Draft in Product Master. Enrich, price, QA and publish before it reaches the Tenant Catalogue.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <Input placeholder="Product name *" value={form.product_name} onChange={set("product_name")} data-testid="wb-add-name" />
          <div className="grid grid-cols-2 gap-2.5">
            <Input placeholder="Brand" value={form.brand} onChange={set("brand")} data-testid="wb-add-brand" list="wb-brands" />
            <Input placeholder="Part Number / SKU" value={form.sku} onChange={set("sku")} data-testid="wb-add-sku" />
          </div>
          <Input placeholder="Category" value={form.category} onChange={set("category")} data-testid="wb-add-category" list="wb-cats" />
          <Input placeholder="Short description (optional)" value={form.description} onChange={set("description")} />
          {dup && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[12px]" data-testid="wb-add-dup">
              <div className="font-medium text-amber-900">Existing product found: {dup.product_name}</div>
              <div className="text-amber-700">{dup.brand} · {dup.sku || dup.oem_part_number}</div>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => submit(true)} disabled={busy} data-testid="wb-add-variant">Create variant anyway</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="create" className="bg-slate-900" onClick={() => submit(false)} disabled={busy} data-testid="wb-add-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add product"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SEV = { ERROR: { i: XCircle, c: "text-rose-600" }, WARN: { i: AlertTriangle, c: "text-amber-600" }, INFO: { i: CheckCircle2, c: "text-slate-500" } };

function QADialog({ ids, onClose }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.post("/catalogue-workbench/qa", { ids }).then(({ data }) => setRes(data)).catch(() => setRes({ results: [], passed: 0, failed: 0 }));
  }, [ids]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="wb-qa-dialog">
        <DialogHeader>
          <DialogTitle>Catalogue QA</DialogTitle>
          <DialogDescription>{ids.length} product(s) checked for duplicates, images, specs, pricing, GST, HSN, warranty and visibility.</DialogDescription>
        </DialogHeader>
        {!res ? <div className="py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Running…</div> : (
          <>
            <div className="flex gap-4 text-sm mb-2"><span className="text-emerald-700 font-semibold">{res.passed} passed</span><span className="text-rose-600 font-semibold">{res.failed} with errors</span></div>
            <div className="space-y-2">
              {res.results.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-md p-2.5">
                  <div className="flex items-center gap-2 text-[13px] font-medium">{r.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}{r.product_name}</div>
                  {r.issues.length > 0 && <ul className="mt-1 space-y-0.5">{r.issues.map((it, i) => { const S = SEV[it.severity] || SEV.INFO; return <li key={i} className="text-[12px] flex items-center gap-1.5"><S.i className={`h-3.5 w-3.5 ${S.c}`} />{it.message}</li>; })}</ul>}
                </div>
              ))}
            </div>
          </>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ ids, tasks, onClose, onDone }) {
  const [form, setForm] = useState({ assigned_to: "", assigned_task: tasks[0] || "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (ids.length === 1) await api.post(`/catalogue-workbench/products/${ids[0]}/assign`, form);
      else await api.post("/catalogue-workbench/bulk", { action: "assign", ids, payload: form });
      toast.success(`Assigned ${ids.length} product(s)`); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="wb-assign-dialog">
        <DialogHeader><DialogTitle>Assign {ids.length} product(s)</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <Input placeholder="Assignee email" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="wb-assign-email" />
          <Select value={form.assigned_task} onValueChange={(v) => setForm({ ...form, assigned_task: v })}>
            <SelectTrigger className="h-9" data-testid="wb-assign-task"><SelectValue placeholder="Task" /></SelectTrigger>
            <SelectContent>{tasks.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="wb-assign-due" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-slate-900" onClick={submit} disabled={busy} data-testid="wb-assign-confirm">Assign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IntelDialog({ row, onClose, onDone }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(true);
  const [applying, setApplying] = useState(false);
  const [opts, setOpts] = useState({ apply_specs: true, apply_description: true, apply_warranty: true });
  useEffect(() => {
    api.post("/oem/fetch", { brand: row.brand || "AUTO", part_number: row.part_number, region: "INDIA" })
      .then(({ data }) => setRes(data)).catch((e) => setRes({ error: e.response?.data?.detail || "Fetch failed" }))
      .finally(() => setBusy(false));
  }, [row]);
  const exact = res?.match_status === "EXACT";
  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/catalogue-workbench/products/${row.id}/apply-intelligence`, { brand: res.brand, part_number: res.part_number, ...opts });
      toast.success(`Applied to product — completeness ${data.completeness.score}% · v${data.product.version}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?._rawDetail?.message || e.response?.data?.detail || "Apply failed"); }
    setApplying(false);
  };
  const n = res?.normalised || {};
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="wb-intel-dialog">
        <DialogHeader>
          <DialogTitle>Product Intelligence · {row.part_number}</DialogTitle>
          <DialogDescription>Live OEM lookup preview for {row.product_name}. Review before applying to the product.</DialogDescription>
        </DialogHeader>
        {busy ? <div className="py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Fetching…</div> : (
          <>
            {exact && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-1.5" data-testid="wb-intel-exact">
                <div className="text-[12px] font-medium text-emerald-900">Exact match: {res.product_name || n.product_name}</div>
                {n.warranty && <div className="text-[11px] text-slate-700">Warranty: {n.warranty}</div>}
                {res.smart_description && <div className="text-[11px] text-slate-600 whitespace-pre-wrap max-h-28 overflow-y-auto">{res.smart_description}</div>}
                <div className="flex flex-wrap gap-3 pt-1 text-[11px]">
                  {[["apply_specs", "Specifications"], ["apply_description", "Description"], ["apply_warranty", "Warranty"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} data-testid={`wb-intel-opt-${k}`} />{l}</label>
                  ))}
                </div>
              </div>
            )}
            <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-md p-3 overflow-x-auto whitespace-pre-wrap max-h-60" data-testid="wb-intel-result">{JSON.stringify(res, null, 2)}</pre>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {exact && <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={apply} disabled={applying} data-testid="wb-intel-apply">{applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" />Apply to product</>}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ id, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/catalogue-workbench/preview/${id}`).then(({ data }) => setP(data)).catch(() => setP({ error: true })); }, [id]);
  const prod = p?.product;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="wb-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview as Tenant</DialogTitle>
          <DialogDescription>Exactly what a tenant sees — no internal cost or margin.</DialogDescription>
        </DialogHeader>
        {!p ? <div className="py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div> : prod ? (
          <div className="space-y-2">
            {prod.primary_image?.url && <img src={imgUrl(prod.primary_image.url)} alt="" className="h-40 mx-auto object-contain" />}
            <div className="text-lg font-semibold text-slate-900">{prod.product_name}</div>
            <div className="text-sm text-slate-500">{prod.brand} · {prod.category}</div>
            <div className="text-sm text-slate-700">{prod.description || "No description"}</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {prod.warranty && <Badge className="bg-slate-100 text-slate-700 border-slate-200">Warranty: {prod.warranty}</Badge>}
              {prod.hsn_sac && <Badge className="bg-slate-100 text-slate-700 border-slate-200">HSN {prod.hsn_sac}</Badge>}
              <Badge className="bg-slate-900 text-white">{p.selling_mode}</Badge>
            </div>
            {p.selling_mode === "PRICE VISIBLE" && prod.standard_selling_price != null && (
              <div className="text-xl font-semibold text-slate-900">₹{Number(prod.standard_selling_price).toLocaleString("en-IN")}</div>
            )}
          </div>
        ) : <div className="py-6 text-center text-rose-500">Could not load preview.</div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
